/**
 * Applies the user's saved language once the session has loaded.
 *
 * Language cannot be resolved at boot: `initI18n` runs before authentication
 * (the sign-in screen needs text too), so the stored value is adopted here as
 * soon as `getSession` answers. That means a signed-in user whose browser is
 * English but whose saved language is Afrikaans sees English for the first
 * moment. Caching the choice client-side would remove that, at the cost of
 * keeping settings on the client — see usePreferences.
 *
 * This lives in the app rather than @mmm/ui because it needs a session; the
 * public site has no signed-in user and keeps the browser-detected language.
 */
import { useEffect } from "react";
import { usePreferences } from "./usePreferences.js";
import { getLanguage, isLanguageCode, setLanguage } from "@mmm/ui";

export function useServerLanguage(): void {
  const { preferences } = usePreferences();
  const saved = preferences.language;

  useEffect(() => {
    // Null means "never chosen" — leave the browser-detected language alone
    // rather than forcing English over it.
    if (!isLanguageCode(saved)) return;
    if (saved === getLanguage()) return;
    void setLanguage(saved);
  }, [saved]);
}
